// Pipeline node registry — declares each node's sockets, defaults, and palette grouping.
// Adding a node = add a NodeSpec here + an executor (executors.ts) + a component (components/pipeline/nodes).

import { NodeSpec, SocketType } from './types';

export const SOCKET_COLORS: Record<SocketType, string> = {
  STRING: '#60a5fa', // info blue
  IMAGE: '#2dd4bf', // teal
  MASK: '#a78bfa', // violet
  NUMBER: '#fb923c', // orange
  DIMENSIONS: '#f59e0b', // amber
  DETECTIONS: '#f43f5e', // rose
  SVG: '#34d399', // green
  VIDEO: '#e879f9', // fuchsia
  ANY: '#9ca3af', // neutral gray
};

export const NODE_SPECS: Record<string, NodeSpec> = {
  promptInput: {
    type: 'promptInput',
    label: 'Prompt',
    category: 'Input',
    inputs: [{ id: 'input', label: 'Text in', type: 'STRING' }],
    outputs: [{ id: 'text', label: 'Text', type: 'STRING' }],
    defaultData: { text: '' },
  },
  generate: {
    type: 'generate',
    label: 'Generate (T2I)',
    category: 'Generator',
    inputs: [
      { id: 'prompt', label: 'Prompt', type: 'STRING' },
      { id: 'seed', label: 'Seed', type: 'NUMBER' },
    ],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: {
      model: 'z-image-turbo',
      width: 1024,
      height: 1024,
      num_inference_steps: 9,
      seed: null,
    },
  },
  textInput: {
    type: 'textInput',
    label: 'Text',
    category: 'Input',
    inputs: [],
    outputs: [{ id: 'text', label: 'Text', type: 'STRING' }],
    defaultData: { text: '', param: null },
  },
  numberInput: {
    type: 'numberInput',
    label: 'Number',
    category: 'Input',
    inputs: [],
    outputs: [{ id: 'value', label: 'Value', type: 'NUMBER' }],
    defaultData: { value: 0, param: null },
  },
  imageUpload: {
    type: 'imageUpload',
    label: 'Image (Upload)',
    category: 'Input',
    inputs: [],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: { image: null, name: null },
  },
  imageGallery: {
    type: 'imageGallery',
    label: 'Image (Gallery)',
    category: 'Input',
    inputs: [],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: { filename: null, imageUrl: null, thumbUrl: null },
  },
  removeBackground: {
    type: 'removeBackground',
    label: 'Remove Background',
    category: 'Operator',
    inputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: { model: 'u2net', alpha_matting: false },
    params: [
      {
        key: 'model',
        label: 'Model',
        kind: 'select',
        options: [
          { value: 'u2net', label: 'u2net (general)' },
          { value: 'u2netp', label: 'u2netp (light)' },
          { value: 'u2net_human_seg', label: 'human' },
          { value: 'isnet-general-use', label: 'isnet general' },
          { value: 'isnet-anime', label: 'isnet anime' },
          { value: 'silueta', label: 'silueta' },
        ],
      },
      { key: 'alpha_matting', label: 'Alpha matting', kind: 'checkbox' },
    ],
  },
  upscale: {
    type: 'upscale',
    label: 'Upscale',
    category: 'Operator',
    inputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: { scale: '2' },
    params: [
      {
        key: 'scale',
        label: 'Scale',
        kind: 'select',
        options: [
          { value: '2', label: '2×' },
          { value: '4', label: '4×' },
        ],
      },
    ],
  },
  faceRestore: {
    type: 'faceRestore',
    label: 'Face Restore',
    category: 'Operator',
    inputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: { upscale: 2 },
    params: [{ key: 'upscale', label: 'Upscale', kind: 'range', min: 1, max: 4, step: 1 }],
  },
  depth: {
    type: 'depth',
    label: 'Depth Map',
    category: 'Operator',
    inputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: {},
  },
  resize: {
    type: 'resize',
    label: 'Resize',
    category: 'Operator',
    livePreview: true,
    inputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: { width: 512, height: 512 },
    params: [
      { key: 'width', label: 'Width', kind: 'number', min: 1, max: 4096, step: 1 },
      { key: 'height', label: 'Height', kind: 'number', min: 1, max: 4096, step: 1 },
    ],
  },
  crop: {
    type: 'crop',
    label: 'Crop',
    category: 'Operator',
    inputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: { rect: { x: 0, y: 0, w: 1, h: 1 } },
  },
  autoTrim: {
    type: 'autoTrim',
    label: 'Auto Trim',
    category: 'Operator',
    livePreview: true,
    inputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    // Crop to content: alpha bbox when the image has transparency, else trim a
    // near-uniform background color sampled from the top-left corner.
    defaultData: { threshold: 10 },
    params: [{ key: 'threshold', label: 'Threshold', kind: 'range', min: 0, max: 254, step: 1 }],
  },
  padFrame: {
    type: 'padFrame',
    label: 'Pad / Frame',
    category: 'Operator',
    livePreview: true,
    inputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    // Center on a (square) transparent canvas with a margin, then resize.
    defaultData: { size: 256, margin: 6, square: true },
    params: [
      { key: 'size', label: 'Size (px)', kind: 'number', min: 16, max: 2048, step: 1 },
      { key: 'margin', label: 'Margin %', kind: 'range', min: 0, max: 50, step: 1 },
      { key: 'square', label: 'Square', kind: 'checkbox' },
    ],
  },
  blur: {
    type: 'blur',
    label: 'Blur',
    category: 'Operator',
    livePreview: true,
    inputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: { radius: 1 },
    params: [{ key: 'radius', label: 'Radius (px)', kind: 'range', min: 0, max: 20, step: 0.5 }],
  },
  seamless: {
    type: 'seamless',
    label: 'Make Seamless',
    category: 'Operator',
    livePreview: true,
    inputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    // Offset-and-cross-blend so the texture tiles with no visible seam.
    defaultData: { band: 16, denoise: 0.6 },
    params: [
      { key: 'band', label: 'Blend band %', kind: 'range', min: 0, max: 40, step: 1 },
      { key: 'denoise', label: 'Denoise px', kind: 'range', min: 0, max: 3, step: 0.1 },
    ],
  },

  // ===== Draw / procedural (synthesize + compose images, no GPU) =====
  solidColor: {
    type: 'solidColor',
    label: 'Solid Color',
    category: 'Draw',
    livePreview: true,
    inputs: [],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: { width: 512, height: 512, color: '#3b82f6', alpha: 255 },
    params: [
      { key: 'width', label: 'Width', kind: 'number', min: 1, max: 4096, step: 1 },
      { key: 'height', label: 'Height', kind: 'number', min: 1, max: 4096, step: 1 },
      { key: 'color', label: 'Color', kind: 'color' },
      { key: 'alpha', label: 'Alpha', kind: 'range', min: 0, max: 255, step: 1 },
    ],
  },
  gradient: {
    type: 'gradient',
    label: 'Gradient',
    category: 'Draw',
    livePreview: true,
    inputs: [],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: { width: 512, height: 512, color1: '#1e293b', color2: '#f59e0b', type: 'vertical' },
    params: [
      { key: 'width', label: 'Width', kind: 'number', min: 1, max: 4096, step: 1 },
      { key: 'height', label: 'Height', kind: 'number', min: 1, max: 4096, step: 1 },
      { key: 'color1', label: 'From', kind: 'color' },
      { key: 'color2', label: 'To', kind: 'color' },
      {
        key: 'type',
        label: 'Direction',
        kind: 'select',
        options: [
          { value: 'horizontal', label: 'Horizontal' },
          { value: 'vertical', label: 'Vertical' },
          { value: 'diagonal', label: 'Diagonal' },
          { value: 'radial', label: 'Radial' },
        ],
      },
    ],
  },
  grid: {
    type: 'grid',
    label: 'Grid / Checker',
    category: 'Draw',
    livePreview: true,
    inputs: [],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: { width: 512, height: 512, cell: 32, color1: '#e5e7eb', color2: '#9ca3af', mode: 'checker', lineWidth: 2 },
    params: [
      { key: 'width', label: 'Width', kind: 'number', min: 1, max: 4096, step: 1 },
      { key: 'height', label: 'Height', kind: 'number', min: 1, max: 4096, step: 1 },
      { key: 'cell', label: 'Cell px', kind: 'range', min: 2, max: 256, step: 1 },
      { key: 'color1', label: 'Color 1', kind: 'color' },
      { key: 'color2', label: 'Color 2', kind: 'color' },
      {
        key: 'mode',
        label: 'Mode',
        kind: 'select',
        options: [
          { value: 'checker', label: 'Checker' },
          { value: 'lines', label: 'Lines' },
        ],
      },
      { key: 'lineWidth', label: 'Line px', kind: 'range', min: 1, max: 16, step: 1 },
    ],
  },
  noise: {
    type: 'noise',
    label: 'Noise',
    category: 'Draw',
    livePreview: true,
    inputs: [],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: { width: 512, height: 512, amount: 64, mono: false },
    params: [
      { key: 'width', label: 'Width', kind: 'number', min: 1, max: 4096, step: 1 },
      { key: 'height', label: 'Height', kind: 'number', min: 1, max: 4096, step: 1 },
      { key: 'amount', label: 'Amount', kind: 'range', min: 0, max: 128, step: 1 },
      { key: 'mono', label: 'Monochrome', kind: 'checkbox' },
    ],
  },
  text: {
    type: 'text',
    label: 'Render Text',
    category: 'Draw',
    livePreview: true,
    inputs: [
      { id: 'text', label: 'Text', type: 'STRING' },
      { id: 'image', label: 'On image', type: 'IMAGE' },
    ],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: {
      width: 512, height: 256, size: 48, color: '#ffffff', x: 0.5, y: 0.5,
      align: 'center', strokeWidth: 0, strokeColor: '#000000',
    },
    params: [
      { key: 'width', label: 'Width (no image)', kind: 'number', min: 1, max: 4096, step: 1 },
      { key: 'height', label: 'Height (no image)', kind: 'number', min: 1, max: 4096, step: 1 },
      { key: 'size', label: 'Font px', kind: 'range', min: 8, max: 256, step: 1 },
      { key: 'color', label: 'Color', kind: 'color' },
      { key: 'x', label: 'X', kind: 'range', min: 0, max: 1, step: 0.01 },
      { key: 'y', label: 'Y', kind: 'range', min: 0, max: 1, step: 0.01 },
      {
        key: 'align',
        label: 'Align',
        kind: 'select',
        options: [
          { value: 'left', label: 'Left' },
          { value: 'center', label: 'Center' },
          { value: 'right', label: 'Right' },
        ],
      },
      { key: 'strokeWidth', label: 'Stroke px', kind: 'range', min: 0, max: 10, step: 1 },
      { key: 'strokeColor', label: 'Stroke', kind: 'color' },
    ],
  },
  shapes: {
    type: 'shapes',
    label: 'Shape / Line',
    category: 'Draw',
    livePreview: true,
    inputs: [{ id: 'image', label: 'On image', type: 'IMAGE' }],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: {
      width: 512, height: 512, shape: 'rectangle', x1: 0.2, y1: 0.2, x2: 0.8, y2: 0.8,
      fill: '#f59e0b', fillAlpha: 255, stroke: '#000000', strokeWidth: 0,
    },
    params: [
      { key: 'width', label: 'Width (no image)', kind: 'number', min: 1, max: 4096, step: 1 },
      { key: 'height', label: 'Height (no image)', kind: 'number', min: 1, max: 4096, step: 1 },
      {
        key: 'shape',
        label: 'Shape',
        kind: 'select',
        options: [
          { value: 'rectangle', label: 'Rectangle' },
          { value: 'ellipse', label: 'Ellipse' },
          { value: 'line', label: 'Line' },
        ],
      },
      { key: 'x1', label: 'X1', kind: 'range', min: 0, max: 1, step: 0.01 },
      { key: 'y1', label: 'Y1', kind: 'range', min: 0, max: 1, step: 0.01 },
      { key: 'x2', label: 'X2', kind: 'range', min: 0, max: 1, step: 0.01 },
      { key: 'y2', label: 'Y2', kind: 'range', min: 0, max: 1, step: 0.01 },
      { key: 'fill', label: 'Fill', kind: 'color' },
      { key: 'fillAlpha', label: 'Fill alpha', kind: 'range', min: 0, max: 255, step: 1 },
      { key: 'stroke', label: 'Stroke', kind: 'color' },
      { key: 'strokeWidth', label: 'Stroke px', kind: 'range', min: 0, max: 20, step: 1 },
    ],
  },
  outline: {
    type: 'outline',
    label: 'Outline',
    category: 'Draw',
    livePreview: true,
    inputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    // Add a colored stroke around the alpha silhouette (e.g. sticker / sprite outline).
    defaultData: { width: 4, color: '#000000', alpha: 255 },
    params: [
      { key: 'width', label: 'Width px', kind: 'range', min: 1, max: 24, step: 1 },
      { key: 'color', label: 'Color', kind: 'color' },
      { key: 'alpha', label: 'Alpha', kind: 'range', min: 0, max: 255, step: 1 },
    ],
  },
  composite: {
    type: 'composite',
    label: 'Composite',
    category: 'Draw',
    livePreview: true,
    inputs: [
      { id: 'background', label: 'Background', type: 'IMAGE' },
      { id: 'foreground', label: 'Foreground', type: 'IMAGE' },
      { id: 'mask', label: 'Mask', type: 'MASK' },
    ],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: { x: 0.5, y: 0.5, scale: 1, opacity: 100, blend: 'normal' },
    params: [
      { key: 'x', label: 'X', kind: 'range', min: 0, max: 1, step: 0.01 },
      { key: 'y', label: 'Y', kind: 'range', min: 0, max: 1, step: 0.01 },
      { key: 'scale', label: 'Scale', kind: 'range', min: 0.05, max: 3, step: 0.05 },
      { key: 'opacity', label: 'Opacity %', kind: 'range', min: 0, max: 100, step: 1 },
      {
        key: 'blend',
        label: 'Blend',
        kind: 'select',
        options: [
          { value: 'normal', label: 'Normal' },
          { value: 'multiply', label: 'Multiply' },
          { value: 'screen', label: 'Screen' },
          { value: 'overlay', label: 'Overlay' },
          { value: 'darken', label: 'Darken' },
          { value: 'lighten', label: 'Lighten' },
          { value: 'add', label: 'Add (linear dodge)' },
          { value: 'difference', label: 'Difference' },
          { value: 'soft-light', label: 'Soft Light' },
          { value: 'hard-light', label: 'Hard Light' },
        ],
      },
    ],
  },
  applyMask: {
    type: 'applyMask',
    label: 'Apply Mask',
    category: 'Draw',
    livePreview: true,
    inputs: [
      { id: 'image', label: 'Image', type: 'IMAGE' },
      { id: 'mask', label: 'Mask', type: 'MASK' },
    ],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: { invert: false },
    params: [{ key: 'invert', label: 'Invert mask', kind: 'checkbox' }],
  },
  adjustColors: {
    type: 'adjustColors',
    label: 'Adjust Colors',
    category: 'Operator',
    livePreview: true,
    inputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    outputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    defaultData: {
      brightness: 100,
      contrast: 100,
      saturation: 100,
      hue: 0,
      rGain: 100,
      gGain: 100,
      bGain: 100,
      grayscale: false,
      invert: false,
      normalize: false,
    },
    params: [
      { key: 'brightness', label: 'Brightness %', kind: 'range', min: 0, max: 300, step: 5 },
      { key: 'contrast', label: 'Contrast %', kind: 'range', min: 0, max: 300, step: 5 },
      { key: 'saturation', label: 'Saturation %', kind: 'range', min: 0, max: 300, step: 5 },
      { key: 'hue', label: 'Hue °', kind: 'range', min: 0, max: 360, step: 5 },
      { key: 'rGain', label: 'Red %', kind: 'range', min: 0, max: 300, step: 5 },
      { key: 'gGain', label: 'Green %', kind: 'range', min: 0, max: 300, step: 5 },
      { key: 'bGain', label: 'Blue %', kind: 'range', min: 0, max: 300, step: 5 },
      { key: 'grayscale', label: 'Grayscale', kind: 'checkbox' },
      { key: 'invert', label: 'Invert', kind: 'checkbox' },
      { key: 'normalize', label: 'Normalize', kind: 'checkbox' },
    ],
  },
  detect: {
    type: 'detect',
    label: 'Detect (YOLO)',
    category: 'Operator',
    inputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    outputs: [
      { id: 'detections', label: 'Boxes', type: 'DETECTIONS' },
      { id: 'image', label: 'Annotated', type: 'IMAGE' },
    ],
    defaultData: { confidence: 0.25 },
    params: [{ key: 'confidence', label: 'Confidence', kind: 'range', min: 0, max: 1, step: 0.05 }],
  },
  segment: {
    type: 'segment',
    label: 'Segment (SAM2)',
    category: 'Operator',
    inputs: [
      { id: 'image', label: 'Image', type: 'IMAGE' },
      { id: 'boxes', label: 'Boxes', type: 'DETECTIONS' },
    ],
    outputs: [{ id: 'mask', label: 'Mask', type: 'MASK' }],
    defaultData: {},
  },
  caption: {
    type: 'caption',
    label: 'Caption',
    category: 'Operator',
    inputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    outputs: [{ id: 'text', label: 'Text', type: 'STRING' }],
    defaultData: {},
  },
  pick: {
    type: 'pick',
    label: 'Pick (Switch)',
    category: 'Operator',
    inputs: [
      { id: 'in1', label: 'In 1', type: 'ANY' },
      { id: 'in2', label: 'In 2', type: 'ANY' },
      { id: 'in3', label: 'In 3', type: 'ANY' },
      { id: 'in4', label: 'In 4', type: 'ANY' },
      { id: 'index', label: 'Index', type: 'NUMBER' },
    ],
    outputs: [{ id: 'out', label: 'Out', type: 'ANY' }],
    defaultData: { index: '1' },
    params: [
      {
        key: 'index',
        label: 'Pick input',
        kind: 'select',
        options: [
          { value: '1', label: 'In 1' },
          { value: '2', label: 'In 2' },
          { value: '3', label: 'In 3' },
          { value: '4', label: 'In 4' },
        ],
      },
    ],
  },
  textJoin: {
    type: 'textJoin',
    label: 'Text Join',
    category: 'Operator',
    inputs: [
      { id: 'in1', label: 'In 1', type: 'STRING' },
      { id: 'in2', label: 'In 2', type: 'STRING' },
      { id: 'in3', label: 'In 3', type: 'STRING' },
      { id: 'in4', label: 'In 4', type: 'STRING' },
    ],
    outputs: [{ id: 'text', label: 'Text', type: 'STRING' }],
    defaultData: { separator: ', ' },
    params: [
      {
        key: 'separator',
        label: 'Separator',
        kind: 'select',
        options: [
          { value: ', ', label: 'comma' },
          { value: ' ', label: 'space' },
          { value: '\n', label: 'newline' },
          { value: '\n\n', label: 'blank line' },
          { value: '', label: 'none' },
        ],
      },
    ],
  },
  animateVideo: {
    type: 'animateVideo',
    label: 'Animate (I2V)',
    category: 'Generator',
    inputs: [
      { id: 'image', label: 'Image', type: 'IMAGE' },
      { id: 'prompt', label: 'Motion', type: 'STRING' },
    ],
    outputs: [{ id: 'video', label: 'Video', type: 'VIDEO' }],
    defaultData: { num_frames: 97 },
    params: [{ key: 'num_frames', label: 'Frames', kind: 'range', min: 25, max: 257, step: 8 }],
  },
  saveToGallery: {
    type: 'saveToGallery',
    label: 'Save to Gallery',
    category: 'Output',
    inputs: [{ id: 'image', label: 'Image', type: 'IMAGE' }],
    outputs: [],
    defaultData: {},
  },
  preview: {
    type: 'preview',
    label: 'Preview',
    category: 'Output',
    inputs: [{ id: 'image', label: 'In', type: 'ANY' }],
    outputs: [],
    defaultData: {},
  },
};

// Order shown in the node palette.
export const PALETTE: string[] = [
  'promptInput',
  'textInput',
  'numberInput',
  'imageUpload',
  'imageGallery',
  'generate',
  'removeBackground',
  'upscale',
  'faceRestore',
  'depth',
  'resize',
  'crop',
  'autoTrim',
  'padFrame',
  'adjustColors',
  'blur',
  'seamless',
  'solidColor',
  'gradient',
  'grid',
  'noise',
  'text',
  'shapes',
  'outline',
  'composite',
  'applyMask',
  'detect',
  'segment',
  'caption',
  'textJoin',
  'pick',
  'animateVideo',
  'preview',
  'saveToGallery',
];
